import React, { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Nav from "components/layout/Nav";
import { TravelMenubar } from "components/layout/MenuBar";
import { DataTable } from "components/shared/table";
import { IoIosCloseCircle } from "react-icons/io";
import { Overview } from "components/forms/overview";
import {
    fetchLocations,
    createLocation,
    softDeleteLocation,
    fetchTrashedLocations,
} from "../../../Slices/travel.slice";
import { CreateLocation } from "components/shared/buttons";
import { canAccessSegment, filterByLocationAccess } from "utils/accessControl";
import { showToast } from "components/shared/Toast";
import { useGetEditorsQuery } from "Slices/rollManager/subAdmin.slice";
import { buildEditDetailsPath } from "utils/locationSlug";

const TravelCMS = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { form } = useParams();

    const { locations = [], loading = false, error = null } = useSelector(
        (state) => state.travel || {}
    );
    const { user } = useSelector((state) => state.auth);
    const isSuperAdmin = user?.role === "superAdmin" || user?.isSuperAdmin;
    const isDistrictManager = user?.role === "districtManager";
    const isEditor = user?.role === "editor";
    const isManager = user?.role === "districtManager" || user?.role === "subAdmin";
    const { data: managedEditors = [] } = useGetEditorsQuery(user?.id, {
        skip: !isManager || !user?.id,
    });

    const [showForm, setShowForm] = useState(false);
    const [activeComponent, setActiveComponent] = useState(form || "AllLocationsTable");
    const [searchTerm, setSearchTerm] = useState("");

    // ✅ Fetch locations and trashed items on mount
    useEffect(() => {
        dispatch(fetchLocations());
        dispatch(fetchTrashedLocations());
    }, [dispatch]);

    // ✅ Update component state based on URL change
    useEffect(() => {
        if (form) {
            setActiveComponent(form);
        }
    }, [form]);

    // ✅ Handle navigation with dynamic URL updates
    const handleComponentSelection = (componentName) => {
        setActiveComponent(componentName);
        navigate(`/cms/travel/${componentName}`, { replace: true });
    };

    const handleAddTravelClick = () => setShowForm(true);
    const closePopup = () => setShowForm(false);

    const handleFormSubmit = async (formData) => {
        try {
            const result = await dispatch(createLocation(formData)).unwrap();

            if (result) {
                console.log("✅ Location created successfully!", result);
                setShowForm(false);
                handleComponentSelection("AllLocationsTable");
                dispatch(fetchLocations());
                dispatch(fetchTrashedLocations());
                showToast("Location saved and sent for approval.", "success");
                return Promise.resolve();
            } else {
                console.error("❌ Failed to add location");
                showToast("Failed to create location.", "error");
                return Promise.reject("Failed to create location");
            }
        } catch (error) {
            console.error("❌ Error in handleFormSubmit:", error);
            showToast("Failed to create location.", "error");
            return Promise.reject(error);
        }
    };


    // ✅ Handle soft delete (move to Trash)
    const handleDelete = (id) => {
        dispatch(softDeleteLocation(id)).then((result) => {
            if (result.type.endsWith("/fulfilled")) {
                dispatch(fetchLocations());
                dispatch(fetchTrashedLocations());
            } else {
                console.error("Failed to soft delete location");
            }
        });
    };

    // ✅ Navigate to Edit Page
    const handleEditClick = (location) => {
        navigate(buildEditDetailsPath("travel", location, "Overview"));
    };

    // ✅ Filter locations based on search term
    const managedEditorIds = useMemo(
        () => new Set((managedEditors || []).map((editor) => editor.id)),
        [managedEditors]
    );
    const locationScoped = useMemo(
        () => filterByLocationAccess(locations || [], user),
        [locations, user]
    );
    const locationScopedIds = useMemo(
        () => new Set((locationScoped || []).map((loc) => loc._id)),
        [locationScoped]
    );
    const allowedLocations = useMemo(() => {
        if (isSuperAdmin) return locations || [];
        if (isEditor) return locations || [];
        if (!isManager) return locationScoped;
        return (locations || []).filter(
            (loc) =>
                locationScopedIds.has(loc._id) ||
                managedEditorIds.has(loc.createdById) ||
                loc.createdById === user?.id
        );
    }, [locations, isSuperAdmin, isEditor, isManager, locationScoped, locationScopedIds, managedEditorIds, user?.id]);

    const cleanedLocations = allowedLocations
        .filter((loc) =>
            !loc.isDeleted &&
            ((loc.locationName && loc.locationName.toLowerCase().includes(searchTerm?.toLowerCase() || "")) || false)
        )
        .filter((loc) => {
            if (user?.role === "editor") return loc.createdById === user?.id;
            return true;
        })
        .map((loc) => ({
            ...loc,
            locationName: loc.locationName || "Unknown Name",
            type: loc.type || "Unknown Type",
            updatedAt: loc.updatedAt || new Date().toISOString(),
        }));

    const canManageTravel = canAccessSegment(user, "Travel");

    return (
        <>
            <Nav />
            <div className="row">
                <TravelMenubar
                    setActiveComponent={handleComponentSelection}
                    setSearchTerm={setSearchTerm}
                />
                <main className="adminPageMain adminPageMain--slate">
                    <div className="adminPageShell">
                    {!canManageTravel && (
                        <div style={{ padding: "2rem" }}>
                            <h4 style={{ color: "#ff5151" }}>Access restricted</h4>
                            <p>You do not have permission to manage the Travel segment.</p>
                        </div>
                    )}

                    {canManageTravel && activeComponent === "AllLocationsTable" && (
                        <div className="locationsTableView">
                            <div className="row justify-content-between align-items-center locationsTableToolbar">
                                <CreateLocation buttonText="Add Location" setShowForm={handleAddTravelClick} />
                            </div>
                            {showForm && (
                                <div className="popup overviewPopup">
                                    <div className="row" style={{ justifyContent: "space-between" }}>
                                        <i className="warning">Fill out the location's overview details</i>
                                        <IoIosCloseCircle className="close" onClick={closePopup} />
                                    </div>
                                    <Overview location={""} onSubmit={handleFormSubmit} contentType="travel" />
                                </div>
                            )}
                            {loading ? (
                                <h5>Loading...</h5>
                            ) : error ? (
                                <h5 style={{ color: "red" }}>Error: {error}</h5>
                            ) : cleanedLocations.length === 0 ? (
                                <h5 style={{ color: "#ff5151" }}>
                                    No locations available for your assigned region(s).
                                </h5>
                            ) : (
                                <DataTable
                                    locations={cleanedLocations}
                                    editClick={handleEditClick}
                                    deleteConfirmation={handleDelete}
                                    showCategoery={true}
                                    showStatus
                                    highlightRecent={isSuperAdmin || isDistrictManager}
                                />
                            )}
                        </div>
                    )}

                    {activeComponent === "Overview" && (
                        <Overview location={""} onSubmit={handleFormSubmit} contentType="travel" />
                    )}
                    </div>
                </main>
            </div>
        </>
    );
};

export default TravelCMS;
